/**
 * Indian PIN-code → city/state/country resolver.
 *
 * Hits the free, no-auth, India-only endpoint hosted at
 * `https://api.postalpincode.in/pincode/{pin}`. Response shape:
 *
 *   [{ Status: 'Success' | 'Error', PostOffice: [{ Name, District, State, Country, ... }] }]
 *
 * Multiple post offices map to a single PIN; we take the first one's
 * District / State as the canonical "city, state" pair. Fails closed —
 * if the network is down or the PIN is invalid, returns null and the
 * caller leaves the city/state inputs untouched.
 *
 * Result is cached in-module so repeated lookups (e.g. typing 6 digits
 * back-and-forth) don't spam the upstream.
 */

export interface PincodeResolution {
  city: string;
  state: string;
  country: string;
}

const cache = new Map<string, PincodeResolution | null>();

interface PostOffice {
  Name?: string;
  District?: string;
  State?: string;
  Country?: string;
}

interface PincodeResponse {
  Status?: string;
  PostOffice?: PostOffice[] | null;
  Message?: string;
}

export async function lookupPincode(pin: string): Promise<PincodeResolution | null> {
  const trimmed = (pin ?? '').trim();
  if (!/^\d{6}$/.test(trimmed)) return null;
  if (cache.has(trimmed)) return cache.get(trimmed)!;
  try {
    // 5s soft timeout — the user is waiting at the input field.
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 5000);
    const r = await fetch(`https://api.postalpincode.in/pincode/${trimmed}`, {
      signal: ctl.signal,
    });
    clearTimeout(t);
    if (!r.ok) {
      cache.set(trimmed, null);
      return null;
    }
    const json = (await r.json()) as PincodeResponse[];
    const first = Array.isArray(json) ? json[0] : null;
    const po = first?.PostOffice?.[0];
    if (first?.Status !== 'Success' || !po) {
      cache.set(trimmed, null);
      return null;
    }
    const resolution: PincodeResolution = {
      city: po.District ?? '',
      state: po.State ?? '',
      country: po.Country ?? 'India',
    };
    cache.set(trimmed, resolution);
    return resolution;
  } catch {
    return null;
  }
}
